import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MimicFolderDialogComponent } from '@components/editor/mimic-folder-dialog/mimic-folder-dialog.component';
import { MimicOptionDialogComponent } from '@components/editor/mimic-option-dialog/mimic-option-dialog.component';
import {
    fileOptioinItemList,
    folderOptioinItemList,
    mimicOptioinItemList,
    OptionItem,
    optionName,
} from '@models/editor';
import { EditorService } from '@service/editor.service';
import { MimicFolderService } from '@service/mimic-folder.service';
import { MimicsService } from '@service/mimics.service';
import { Mimic } from 'icreate/common/models/mimic';
import { MimicFolderViewData, RootDirectionOption, RootItem, RootItemType } from 'icreate/common/models/mimic-folder';
import { Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { DuplicateMimicDialogComponent } from '@components/editor/duplicate-mimic-dialog/duplicate-mimic-dialog.component';
import { ButtonLabel, DuplicateItemType } from '@helpers/constants';
import { CanvasService } from '@service/canvas/canvas.service';
import { Utils } from '@helpers/utils';

@Component({
    selector: 'app-mimic-tab',
    templateUrl: './mimic-tab.component.html',
    styleUrls: ['../editor-navigator.component.css', './mimic-tab.component.css'],
    standalone: false,
})
export class MimicTabComponent {
    readonly FOLDER_TYPE = RootItemType.folder;
    readonly MIMIC_TYPE = RootItemType.mimic;

    @Input() rootList: RootItem[];
    @Input() vessel: Vessel;
    @Input() selectedMimic: Mimic;
    @Input() canCreateOrDelete: boolean;

    @Output() onDeleteMimicFolder = new EventEmitter<MimicFolderViewData>();
    @Output() onCreateMimic = new EventEmitter<{ mimicFolderId: string; index: number }>();
    @Output() onDuplicateMimic = new EventEmitter<Mimic>();
    @Output() onDeleteMimic = new EventEmitter<Mimic>();
    @Output() onPrintAll = new EventEmitter<Promise<void>>();
    @Output() onSelectMimic = new EventEmitter<Mimic>();
    @Output() onPrintMimic = new EventEmitter<Mimic>();
    @Output() onOpenExpanded = new EventEmitter<string>();

    currentMimicFolder: MimicFolderViewData;
    currentMimic: Mimic;
    openingFileOptionDialog: boolean = false;
    canvas: any;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private dialog: MatDialog,
        private editorService: EditorService,
        private mimicFolderService: MimicFolderService,
        private mimicService: MimicsService,
        private canvasService: CanvasService
    ) {}

    async ngOnInit() {
        this.canvas = this.canvasService.getCanvas();
        this.editorService.currentMimic$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(mimic => {
            this.selectedMimic = mimic;
        });
    }

    ngOnDestroy() {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    async openFilesOptionDialog(event: MouseEvent) {
        this.openingFileOptionDialog = true;
        try {
            const optionItemList = fileOptioinItemList.map(optionItem => {
                return {
                    ...optionItem,
                    disabled: this.canCreateOrDelete || optionItem.label == optionName.print ? false : true,
                };
            });

            const optionItem = await this.openDialog(event, optionItemList);

            if (optionItem?.label === optionName.folder) {
                this.dialog.open(MimicFolderDialogComponent, {
                    data: {
                        vessel: this.vessel,
                        rootList: this.rootList,
                    },
                    autoFocus: false,
                    disableClose: true,
                });
            } else if (optionItem?.label === optionName.mimic) {
                this.onCreateMimic.emit({ mimicFolderId: null, index: this.rootList.length });
            } else if (optionItem?.label === optionName.nameAsc) {
                await this.mimicFolderService.sortRoot(this.vessel.id, RootDirectionOption.asc);
            } else if (optionItem?.label === optionName.nameDesc) {
                await this.mimicFolderService.sortRoot(this.vessel.id, RootDirectionOption.desc);
            } else if (optionItem?.label === optionName.print) {
                this.onPrintAll.emit();
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.openingFileOptionDialog = false;
        }
    }

    openExpanded(folderId: string) {
        this.onOpenExpanded.emit(folderId);
    }

    async openFolderOptionDialog(event: MouseEvent, mimicFolder: MimicFolderViewData) {
        this.currentMimicFolder = { ...mimicFolder };
        try {
            const optionItemList = folderOptioinItemList.map(optionItem => {
                if (!this.canCreateOrDelete) {
                    return {
                        ...optionItem,
                        disabled: optionItem.label === optionName.print ? false : true,
                    };
                }
                if (optionItem.label === optionName.moveUp) {
                    return {
                        ...optionItem,
                        disabled: this.rootList.length === 1 ? true : mimicFolder.index === 0 ? true : false,
                    };
                } else if (optionItem.label === optionName.moveDown) {
                    return {
                        ...optionItem,
                        disabled:
                            this.rootList.length === 1
                                ? true
                                : mimicFolder.index === this.rootList.length - 1
                                  ? true
                                  : false,
                    };
                }

                return optionItem;
            });

            const optionItem = await this.openDialog(event, optionItemList);

            if (optionItem?.label === optionName.rename) {
                this.dialog.open(MimicFolderDialogComponent, {
                    data: {
                        vessel: this.vessel,
                        mimicFolder: mimicFolder,
                        rootList: this.rootList,
                    },
                    autoFocus: false,
                    disableClose: true,
                });
            } else if (optionItem?.label === optionName.delete) {
                this.onDeleteMimicFolder.emit(mimicFolder);
            } else if (optionItem?.label === optionName.moveUp) {
                await this.mimicFolderService.updateIndex(
                    mimicFolder.index,
                    mimicFolder.index - 1,
                    mimicFolder.vesselId
                );
            } else if (optionItem?.label === optionName.moveDown) {
                await this.mimicFolderService.updateIndex(
                    mimicFolder.index,
                    mimicFolder.index + 1,
                    mimicFolder.vesselId
                );
            } else if (optionItem?.label === optionName.duplicate) {
                const mimics =
                    this.rootList.find(item => item.type === RootItemType.folder && item.folder.id === mimicFolder.id)
                        ?.folder?.mimicList ?? [];

                const folderNames = this.rootList
                    .filter(item => item.type === RootItemType.folder)
                    .map(item => item.folder.name);

                const dialogRef = this.dialog.open(DuplicateMimicDialogComponent, {
                    data: {
                        title: 'mimic.duplicate.duplicate-mimic-folder.title',
                        description: 'mimic.duplicate.duplicate-mimic-folder.description',
                        initialName: mimicFolder.name,
                        existingNames: folderNames,
                        type: DuplicateItemType.FOLDER,
                        submitButtonLabel: ButtonLabel.DUPLICATE,
                        items: mimics,
                        selectionLabel: 'mimic.duplicate.duplicate.item-label.mimic',
                        autoIncrementName: true,
                        showExtraOptions: true,
                    },
                });

                const { name: newName, selectedOptions } = await lastValueFrom(dialogRef.afterClosed());
                if (!newName) return;

                const mimicsWithIdMapping = Utils.mapItemsWithSvgIdMapping(selectedOptions, 'svgcontent', this.canvas);
                const newFolder = {
                    ...mimicFolder,
                    name: newName,
                    mimicList: mimicsWithIdMapping,
                };

                await this.mimicFolderService.duplicateMimicFolder(newFolder);
            } else if (optionItem?.label === optionName.createMimic) {
                this.onCreateMimic.emit({
                    mimicFolderId: mimicFolder.id,
                    index: mimicFolder.mimicList.length,
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.currentMimicFolder = undefined;
        }
    }

    async openMimicOptionDialog(event: MouseEvent, mimic: Mimic) {
        this.currentMimic = mimic;
        try {
            const currentFolder = this.rootList.find(rootItem => {
                if (rootItem.type !== this.FOLDER_TYPE) {
                    return false;
                }

                return rootItem.folder.id === mimic.folderId;
            });

            mimicOptioinItemList[0].subOptionItem[0].subOptionItem = [
                {
                    label: optionName.outOfFolder,
                    arrowIcon: false,
                    new: false,
                    svg: undefined,
                    disabled: mimic.folderId ? false : true,
                },
                ...(this.rootList
                    .filter(rootItem => rootItem.type === this.FOLDER_TYPE && rootItem?.folder.id !== mimic.folderId)
                    .map(rootItem => ({
                        label: rootItem.folder.name,
                        arrowIcon: false,
                        new: false,
                        svg: undefined,
                        disabled: false,
                        folder: rootItem.folder,
                    })) as any),
            ];

            const isInFolder = !!mimic.folderId;

            const rootLen = this.rootList?.length ?? 0;

            const containerLen = isInFolder ? (currentFolder?.folder?.mimicList?.length ?? 0) : rootLen;

            const isOnlyOne = containerLen <= 1;
            const isFirst = mimic.index === 0;
            const isLast = mimic.index === containerLen - 1;

            mimicOptioinItemList[0].subOptionItem = mimicOptioinItemList[0].subOptionItem.map(optionItem => {
                if (!this.canCreateOrDelete) {
                    return { ...optionItem, disabled: true };
                }

                if (optionItem.label === optionName.moveUp) {
                    return { ...optionItem, disabled: isOnlyOne || isFirst };
                }

                if (optionItem.label === optionName.moveDown) {
                    return { ...optionItem, disabled: isOnlyOne || isLast };
                }

                return optionItem;
            });

            const optionItem = await this.openDialog(event, mimicOptioinItemList);
            if (optionItem?.label === optionName.rename) {
            } else if (optionItem?.label === optionName.moveUp) {
                await this.mimicService.updateIndex(mimic.index, mimic.index - 1, mimic.vesselId, mimic.folderId);
            } else if (optionItem?.label === optionName.moveDown) {
                await this.mimicService.updateIndex(mimic.index, mimic.index + 1, mimic.vesselId, mimic.folderId);
            } else if (optionItem?.label === optionName.duplicate) {
                this.onDuplicateMimic.emit(mimic);
            } else if (optionItem?.label === optionName.delete) {
                this.onDeleteMimic.emit(mimic);
            } else if (optionItem?.label === optionName.outOfFolder) {
                const mimics =
                    this.rootList.filter(item => item.type === RootItemType.mimic).map(item => item.mimic) ?? [];
                const mimicNames = mimics.map(mimic => mimic.name);

                let movedMimicName = mimic.name;
                if (mimicNames.includes(mimic.name)) {
                    const dialogRef = this.dialog.open(DuplicateMimicDialogComponent, {
                        data: {
                            title: 'mimic.duplicate.mimic-move-error.title',
                            description: 'mimic.duplicate.mimic-move-error.description',
                            initialName: mimic.name,
                            existingNames: mimicNames,
                            type: DuplicateItemType.MIMIC,
                            items: mimics,
                            submitButtonLabel: ButtonLabel.MOVE,
                            autoIncrementName: false,
                        },
                    });

                    const { name: newName } = await lastValueFrom(dialogRef.afterClosed());
                    if (!newName) return;
                    movedMimicName = newName;
                }

                await this.mimicService.update({
                    ...mimic,
                    name: movedMimicName,
                    folderId: null,
                    index: this.rootList.length,
                });
            } else if (optionItem?.folder) {
                const mimics =
                    this.rootList.find(
                        item => item.type === RootItemType.folder && item.folder.id === optionItem?.folder.id
                    )?.folder?.mimicList ?? [];

                const mimicNames = mimics.map(mimic => mimic.name);
                let movedMimicName = mimic.name;
                if (mimicNames.includes(mimic.name)) {
                    const dialogRef = this.dialog.open(DuplicateMimicDialogComponent, {
                        data: {
                            title: 'mimic.duplicate.mimic-move-error.title',
                            description: 'mimic.duplicate.mimic-move-error.description',
                            initialName: mimic.name,
                            type: DuplicateItemType.MIMIC,
                            existingNames: mimicNames,
                            items: mimics,
                            submitButtonLabel: ButtonLabel.MOVE,
                            autoIncrementName: false,
                        },
                    });
                    const { name: newName } = await lastValueFrom(dialogRef.afterClosed());
                    if (!newName) return;
                    movedMimicName = newName;
                }

                await this.mimicService.update({
                    ...mimic,
                    name: movedMimicName,
                    folderId: optionItem?.folder.id,
                    index: optionItem.folder.mimicList.length,
                });
            } else if (optionItem?.label === optionName.print) {
                this.onPrintMimic.emit(mimic);
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.currentMimic = undefined;
        }
    }

    selectMimic(mimic: Mimic) {
        this.onSelectMimic.emit(mimic);
    }

    private async openDialog(event: MouseEvent, itemList): Promise<OptionItem> {
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();

        const dialogRef = this.dialog.open(MimicOptionDialogComponent, {
            data: {
                optionItemList: itemList,
            },
            backdropClass: 'transparent-backdrop',
            position: {
                top: `${rect.y}px`,
                left: `${rect.x + rect.width + 5}px`,
            },
            disableClose: false,
        });

        return await lastValueFrom(dialogRef.afterClosed());
    }
}
